import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { AssetsRelatedEntity } from '@app/core/model/entities/asset/assets-related-entity';
import { Document } from '@app/core/model/entities/document/document';
import { Entity, IRelatedEntity, RelatedEntity } from '@app/core/model/entities/entity';
import { getValue, hasProperty, setValue } from '@app/shared/extra/utils';
import { Expose, Type } from 'class-transformer';
import { IRelatedAsset, RelatedAsset } from '../asset/asset';

export class Equipment extends Entity implements AssetsRelatedEntity {
  public objectType = EntityTypeEnum.EQUIPMENT;
  public assetId: string;
  public identifier: number;
  public organizationId: string;
  public dataDate: string;

  @Expose({name: 'spaceIdsList'})
  public spaceIds: string[];

  @Expose({name: 'equipmentIdsList'})
  public equipmentIds: string[];

  @Type(() => Document) public documents: Document[] = [];
  @Type(() => Document) public pictures: Document[] = [];

  @Expose()
  public toString(): string {
    return this.computedProperties?.['displayName'] ?? `${this.identifier} - ${this.name}`;
  }

  /**
   * Transforms this Equipment to a RelatedEquipment.
   */
  public toRelatedEquipment(): RelatedEquipment {
    return new RelatedEquipment({
      id: this.id,
      name: this.name,
      identifier: this.identifier,
      displayName: this.computedProperties?.['displayName']
    });
  }

  /**
   * Get the related asset inside the Equipment computed properties.
   * @return List of related assets
   */
  @Expose()
  public get relatedAssets(): RelatedAsset[] {
    return this.computedProperties?.['asset']
        ?.map((asset: IRelatedAsset) => new RelatedAsset(asset))
      ?? [];
  }

  // Properties to be renamed
  protected static inputMapping = {
    asset: {input: ['computedProperties', 'asset', 'id'], output: 'assetId'},
    spaces: {input: ['computedProperties', 'spaces'], output: 'spaceIds'},
    equipments: {
      input: ['computedProperties', 'equipments'],
      output: 'equipmentIds',
      transform: (equipments: RelatedEquipment[]): string[] => equipments.map(e => e.id)
    }
  };

  /**
   * Some GraphQL inputs require different keys than those available in the Form State Service
   * since those keys correspond to field codes, so we need to replace them accordingly
   * @param obj the Graphql input object
   */
  public static transformPropertiesForInput(obj: Record<string, unknown>): void {
    // Transform input, mapping keys as defined in the inputMapping above
    for (const inputMappingElement in Equipment.inputMapping) {
      const inputMapping = Equipment.inputMapping[inputMappingElement];
      if (hasProperty(obj, inputMapping.input)) {
        const source = getValue(obj, inputMapping.input);
        if (inputMapping.transform) {
          setValue(obj, [inputMapping.output], inputMapping.transform(source));
        } else {
          setValue(obj, [inputMapping.output], source);
        }
      }
    }

    // Clean up by removing older keys
    for (const inputMappingElement in Equipment.inputMapping) {
      delete obj[Equipment.inputMapping[inputMappingElement].input.first()];
    }
  }
}

export type IRelatedEquipment = IRelatedEntity;

export class RelatedEquipment extends RelatedEntity implements IRelatedEquipment {
  constructor(relatedEquipment: IRelatedEquipment) {
    super(relatedEquipment);
  }
}

export interface EquipmentInput {
  name?: string;
  state?: string;
  spaceIds?: string[];
  dataDate?: string;
  assetId?: string;
  equipmentIds?: string[];
  properties?: Record<string, any>;
}
